// src/belediye/components/reports/ReportResults.js
//
// DEĞİŞİKLİKLER:
// ─ rr-topbar: "Sonuçlar" başlığı KALDIRILDI — ReportsPage yönetiyor, tekrar yok
// ─ SummaryCard → KpiStrip: 4 kutu yan yana (Toplam / Açık / Kapalı / Çözüm Oranı)
//   Çözüm oranı artık kopuk sağ üstte değil, diğer KPI'larla aynı sırada
// ─ DatasetTabs → rr-tabs: pill/tab, her sekmede sayı badge, aktif dolu zemin
// ─ TableCard başlığı: tek satır (tablo adı sol | kayıt sayısı + export sağ)
//   Export artık tablo başlığıyla gerçek bağ kuruyor
// ─ Tablo: sıkı padding, sticky header, ilk kolon baskın, row hover
// ─ Tüm Redux/state/selector mantığı birebir korundu

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  runReportV1Query,
  selectReportV1Query,
  selectReportV1Summary,
  selectReportV1TablesByDataset,
  selectReportV1Options,
  selectReportV1IsPresidentLike,
  selectReportV1AdvancedOpen,
} from "../../redux/slices/belediyeReportV1Slice";

import ReportsExport from "./ReportsExport";

import {
  InformationCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

import "./ReportResults.css";

/* =============================================================================
   Helpers
============================================================================= */

const ensureArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const isObj       = (x) => x && typeof x === "object" && !Array.isArray(x);

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const pct = (v) => {
  const n = toNum(v);
  if (n === null) return null;
  const p = n <= 1 ? n * 100 : n;
  return `${p.toFixed(1)}%`;
};

const datasetTitleTr = (ds) => {
  switch (ds) {
    case "applications":          return "Başvurular";
    case "status_logs":           return "Durum Logları";
    case "forward_logs":          return "Yönlendirme Logları";
    case "operation_logs":        return "İşlem Logları";
    case "close_request_history": return "Kapatma Talep Geçmişi";
    default:                      return ds || "Tablo";
  }
};

function tryParseJson(x) {
  if (!x) return null;
  if (typeof x === "object") return x;
  if (typeof x !== "string") return null;
  try { return JSON.parse(x); } catch { return null; }
}

/* Dataset key alias'ları — backend farklı key döndürebilir */
const DATASET_KEY_ALIASES = {
  applications:          ["applications"],
  status_logs:           ["status_logs", "status_log", "statuslog", "status_logs_table"],
  forward_logs:          ["forward_logs", "forward_log", "forwardlog", "forward_logs_table"],
  operation_logs:        ["operation_logs", "operation_log", "op_logs", "operation_logs_table"],
  close_request_history: ["close_request_history", "close_request_histories", "close_request_history_table"],
};

const getTableForDataset = (tablesByDataset, ds) => {
  if (!tablesByDataset) return null;
  const aliases = DATASET_KEY_ALIASES[ds] || [ds];
  for (const k of aliases) {
    if (tablesByDataset[k]) return tablesByDataset[k];
  }
  return null;
};

/* Tablo normalize — backend farklı format döndürebilir */
function normalizeTable(table) {
  if (!table) return { columns: [], rows: [], meta: {} };

  const meta    = table.pagination || table.meta || table.page || {};
  const columns =
    ensureArray(table.columns).length  ? ensureArray(table.columns)  :
    ensureArray(table.headers).length  ? ensureArray(table.headers)  :
    ensureArray(table.fields);

  const rawRows =
    ensureArray(table.rows).length  ? table.rows  :
    ensureArray(table.data).length  ? table.data  :
    ensureArray(table.items);

  if (rawRows.length && isObj(rawRows[0])) {
    const inferredCols = columns.length ? columns : Object.keys(rawRows[0] || {});
    return { columns: inferredCols, rows: rawRows, meta };
  }

  if (rawRows.length && Array.isArray(rawRows[0])) {
    const inferredCols = columns.length
      ? columns
      : rawRows[0].map((_, i) => `col_${i + 1}`);
    const mapped = rawRows.map((arr) => {
      const o = {};
      inferredCols.forEach((c, i) => { o[c] = arr[i]; });
      return o;
    });
    return { columns: inferredCols, rows: mapped, meta };
  }

  return { columns, rows: [], meta };
}

const safeCell = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean")        return v ? "Evet" : "Hayır";
  return String(v);
};

function getPaginationFrom(meta, lastParams) {
  const total      = meta?.total ?? meta?.count ?? meta?.total_count ?? null;
  const page       = meta?.page  ?? meta?.page_number ?? lastParams?.page ?? 1;
  const pageSize   = meta?.page_size ?? meta?.per_page ?? lastParams?.page_size ?? 25;
  const totalPages =
    meta?.total_pages ??
    (total !== null
      ? Math.max(1, Math.ceil((toNum(total) ?? 0) / (toNum(pageSize) ?? 25)))
      : null);

  return {
    page:       toNum(page)       || 1,
    pageSize:   toNum(pageSize)   || 25,
    total:      total !== null    ? toNum(total)      ?? total      : null,
    totalPages: totalPages !== null ? toNum(totalPages) ?? totalPages : null,
  };
}

/* =============================================================================
   KpiStrip — 4 kutu yan yana (Toplam / Açık / Kapalı / Çözüm Oranı)
   Çözüm oranı ayrı sağ üstte kopuk değil, aynı sırada.
============================================================================= */

function KpiStrip({ summary }) {
  if (!summary) return null;

  const total      = summary.total      ?? summary.count      ?? null;
  const open       = summary.open       ?? summary.open_count  ?? null;
  const closed     = summary.closed     ?? summary.closed_count ?? null;
  const solvedRate = summary.solved_rate ?? summary.solvedRate  ?? null;

  const byStatus   = summary.by_status || summary.statuses || null;
  const statusPairs = byStatus && isObj(byStatus) ? Object.entries(byStatus) : [];

  return (
    <>
      <div className="rr-kpi-strip">
        <div className="rr-kpi-card rr-kpi-card--total">
          <div className="rr-kpi-card__label">Toplam</div>
          <div className="rr-kpi-card__value">{total ?? "—"}</div>
        </div>

        <div className="rr-kpi-card rr-kpi-card--open">
          <div className="rr-kpi-card__label">Açık</div>
          <div className="rr-kpi-card__value">{open ?? "—"}</div>
        </div>

        <div className="rr-kpi-card rr-kpi-card--closed">
          <div className="rr-kpi-card__label">Kapalı</div>
          <div className="rr-kpi-card__value">{closed ?? "—"}</div>
        </div>

        <div className="rr-kpi-card rr-kpi-card--rate">
          <div className="rr-kpi-card__label">Çözüm Oranı</div>
          <div className="rr-kpi-card__value">
            {solvedRate !== null && solvedRate !== undefined
              ? pct(solvedRate) || String(solvedRate)
              : "—"}
          </div>
        </div>
      </div>

      {/* Durum dağılımı chip'leri */}
      {statusPairs.length > 0 && (
        <div className="rr-kpi-chips">
          {statusPairs.map(([k, v]) => (
            <span key={k} className="rr-kpi-chip">
              <span className="rr-kpi-chip__k">{String(k)}</span>
              <span className="rr-kpi-chip__v">{String(v)}</span>
            </span>
          ))}
        </div>
      )}
    </>
  );
}

/* =============================================================================
   DatasetTabs — pill/tab, badge sayısı, aktif dolu zemin
============================================================================= */

function DatasetTabs({ items, activeKey, onChange }) {
  if (!items?.length) return null;

  return (
    <div className="rr-tabs">
      {items.map((it) => {
        const isActive   = it.key === activeKey;
        const isDisabled = !!it.disabled;

        return (
          <button
            key={it.key}
            type="button"
            className={[
              "rr-tab",
              isActive   ? "rr-tab--active"   : "",
              isDisabled ? "rr-tab--disabled"  : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => !isDisabled && onChange(it.key)}
            disabled={isDisabled}
            title={isDisabled ? it.disabledReason || "Bu tablo hazır değil." : it.label}
          >
            <span className="rr-tab__label">{it.label}</span>
            {typeof it.count === "number" && (
              <span className="rr-tab__badge">{it.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* =============================================================================
   Pagination
============================================================================= */

function Pagination({ page, totalPages, onPrev, onNext }) {
  if (!totalPages || totalPages <= 1) return null;

  return (
    <div className="rr-pager">
      <button
        type="button"
        className="btn-md btn-md-ghost"
        onClick={onPrev}
        disabled={page <= 1}
      >
        ‹ Önceki
      </button>
      <div className="rr-pager__info">
        Sayfa <b>{page}</b> / {totalPages}
      </div>
      <button
        type="button"
        className="btn-md btn-md-ghost"
        onClick={onNext}
        disabled={page >= totalPages}
      >
        Sonraki ›
      </button>
    </div>
  );
}

/* =============================================================================
   TableCard
   - Başlık: tek satır (tablo adı sol | kayıt sayısı pill + export sağ)
   - Export artık tablo başlığıyla birlikte → gerçek bağ kuruldu
   - Tablo: sıkı padding, sticky header, ilk kolon baskın
============================================================================= */

function TableCard({ dataset, table, columnLabel, right, footer }) {
  const { columns, rows, meta } = normalizeTable(table);

  const totalish = meta?.total ?? meta?.count ?? null;
  const rowCount = rows.length;
  const countText = typeof totalish === "number"
    ? `${totalish.toLocaleString("tr-TR")} kayıt`
    : `${rowCount} satır`;

  return (
    <div className="rr-tablecard">

      {/* Başlık — tek satır */}
      <div className="rr-tablecard__head">
        <div className="rr-tablecard__headleft">
          <span className="rr-tablecard__title">
            {datasetTitleTr(dataset)}
          </span>
          <span className="rr-tablecard__count">{countText}</span>
        </div>

        <div className="rr-tablecard__right">
          {right || null}
        </div>
      </div>

      {/* Tablo */}
      <div className="rr-tablewrap">
        <table className="rr-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c}>{columnLabel(dataset, c)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!rowCount ? (
              <tr>
                <td
                  colSpan={Math.max(1, columns.length)}
                  className="rr-emptycell"
                >
                  Bu filtreye ait kayıt bulunamadı.
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr key={idx}>
                  {columns.map((c) => (
                    /* FAZ-103 — `data-label` KALDIRILDI.
                       FAZ-96 §3.2'de telefonda tablo satır satır KARTA
                       dönüyordu ve etiketi bu öznitelik taşıyordu. Kullanıcı
                       (2026-08-02) kart desenini geri aldı: "rapor sayfası
                       mobilde liste hâlindeydi, tabloydu, satır sütun mantığı
                       vardı; bu rapor sayfasını eski hâline getir."
                       Kart deseni kalkınca öznitelik hiçbir yerden okunmuyor —
                       her satırda her sütun için üretilen ölü işaret olurdu. */
                    <td key={c} title={safeCell(r?.[c])}>
                      {safeCell(r?.[c])}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer / pager */}
      {footer && (
        <div className="rr-tablecard__footer">{footer}</div>
      )}
    </div>
  );
}

/* =============================================================================
   ReportResults — ana component
============================================================================= */

export default function ReportResults() {
  const dispatch = useDispatch();

  const queryState      = useSelector(selectReportV1Query);
  const summary         = useSelector(selectReportV1Summary);
  const tablesByDataset = useSelector(selectReportV1TablesByDataset);
  const optionsState    = useSelector(selectReportV1Options);
  const isPresidentLike = useSelector(selectReportV1IsPresidentLike);
  const advancedOpen    = useSelector(selectReportV1AdvancedOpen);

  const optionsData = optionsState?.data || null;
  const lastParams  = queryState?.lastParams || null;

  /* İstenen dataset'ler */
  const requestedDatasets = useMemo(() => {
    const fromQuery = ensureArray(lastParams?.datasets);
    const set       = new Set(["applications"]);
    fromQuery.forEach((d) => { if (d) set.add(d); });
    return Array.from(set);
  }, [lastParams]);

  const loading    = !!queryState?.loading;
  const rawError   = queryState?.error || null;
  const hasResult  = !!queryState?.lastParams;

  /* Error parse */
  const errorObj  = useMemo(() => tryParseJson(rawError), [rawError]);
  const errorText = useMemo(() => {
    if (!rawError) return null;
    if (errorObj?.columns) return `Kolon doğrulama hatası: ${String(errorObj.columns)}`;
    if (errorObj?.detail)  return String(errorObj.detail);
    return String(rawError);
  }, [rawError, errorObj]);

  /* Kolon etiketleri */
  const columnCatalog = optionsData?.columns || {};

  const colByDataset = useMemo(
    () => ({
      applications:          columnCatalog?.applications          || {},
      status_logs:           columnCatalog?.status_logs           || {},
      forward_logs:          columnCatalog?.forward_logs          || {},
      operation_logs:        columnCatalog?.operation_logs        || {},
      close_request_history: columnCatalog?.close_request_history || {},
    }),
    [columnCatalog]
  );

  const columnLabel = useMemo(
    () => (dataset, key) => (colByDataset?.[dataset] || {})?.[key] || key,
    [colByDataset]
  );

  /* ── Sekme listesi ── */
  const tabItems = useMemo(() => {
    const order = [
      "applications",
      "status_logs",
      "forward_logs",
      "operation_logs",
      "close_request_history",
    ];

    if (!advancedOpen) {
      const t = getTableForDataset(tablesByDataset, "applications");
      const { meta, rows } = normalizeTable(t);
      const count =
        typeof (meta?.total ?? meta?.count) === "number"
          ? meta.total ?? meta.count
          : rows.length;

      return [
        {
          key:      "applications",
          label:    datasetTitleTr("applications"),
          count,
          disabled: false,
        },
      ];
    }

    const requested = new Set(requestedDatasets);
    requested.add("applications");

    return order
      .filter((k) => requested.has(k) || k === "applications")
      .map((k) => {
        const t         = getTableForDataset(tablesByDataset, k);
        const available = !!t;
        const { meta, rows } = normalizeTable(t);

        const count =
          available && typeof (meta?.total ?? meta?.count) === "number"
            ? meta.total ?? meta.count
            : available
              ? rows.length
              : null;

        const disabled = k !== "applications" && !available;
        const disabledReason = !available
          ? "Bu dataset sorgudan dönmedi (yetki/kontrat/parametre kontrol edin)."
          : "";

        return {
          key:          k,
          label:        datasetTitleTr(k),
          count:        typeof count === "number" ? count : undefined,
          disabled,
          disabledReason,
        };
      });
  }, [advancedOpen, tablesByDataset, requestedDatasets]);

  /* ── Aktif sekme ── */
  const [activeDataset, setActiveDataset] = useState("applications");

  useEffect(() => {
    if (!advancedOpen) {
      setActiveDataset("applications");
      return;
    }

    const enabledKeys = tabItems.filter((x) => !x.disabled).map((x) => x.key);
    if (!enabledKeys.includes(activeDataset)) {
      setActiveDataset(enabledKeys[0] || "applications");
    }
  }, [advancedOpen, tabItems, activeDataset]);

  /* ── Aktif tablo ── */
  const activeTable = getTableForDataset(tablesByDataset, activeDataset);

  /* ── Sayfalama (sadece applications) ── */
  const appNorm   = normalizeTable(getTableForDataset(tablesByDataset, "applications"));
  const appPager  = getPaginationFrom(appNorm.meta || {}, lastParams || {});
  const showPager = activeDataset === "applications"
    && !!appPager.totalPages
    && appPager.totalPages > 1;

  const onPrevPage = () => {
    const next = Math.max(1, (appPager.page || 1) - 1);
    dispatch(runReportV1Query({ page: next }));
  };

  const onNextPage = () => {
    if (!appPager.totalPages) return;
    const next = Math.min(appPager.totalPages, (appPager.page || 1) + 1);
    dispatch(runReportV1Query({ page: next }));
  };

  /* ── Yardımcı durumlar ── */
  const showAdvancedHint =
    hasResult &&
    isPresidentLike &&
    advancedOpen &&
    requestedDatasets.length === 1 &&
    requestedDatasets[0] === "applications";

  const missingRequested = useMemo(() => {
    if (!advancedOpen) return [];
    return requestedDatasets
      .filter((ds) => ds !== "applications")
      .filter((ds) => !getTableForDataset(tablesByDataset, ds));
  }, [advancedOpen, requestedDatasets, tablesByDataset]);

  /* ── Topbar alt text ── */
  const subText = loading
    ? "Yükleniyor…"
    : errorText
      ? "Sorgu hatası"
      : hasResult
        ? "Filtrelere göre sonuçlar listelendi"
        : "Henüz sorgu çalıştırılmadı";

  /* ══════════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════════════ */
  return (
    <div className="rr-root">

      {/* ── Topbar — "Sonuçlar" başlığı YOK (ReportsPage'de zaten panel başlığı var) ── */}
      <div className="rr-topbar">
        <div className="rr-topbar__sub">{subText}</div>
      </div>

      {/* ── Sorgu hatası ── */}
      {errorText && (
        <div className="rr-state rr-state--error">
          <ExclamationTriangleIcon width={16} height={16} />
          <div>
            <div className="rr-state__title">Sorgu Hatası</div>
            <div className="rr-state__body" style={{ whiteSpace: "pre-wrap" }}>
              {errorText}
            </div>
          </div>
        </div>
      )}

      {/* ── Henüz sorgu yok ── */}
      {!hasResult && !loading && (
        <div className="rr-state">
          <InformationCircleIcon width={16} height={16} />
          <div>
            <div className="rr-state__title">Filtre Uygulayın</div>
            <div className="rr-state__body">
              Filtreleri seçip <b>Uygula</b> butonuna basın.
            </div>
          </div>
        </div>
      )}

      {/* ── KPI şeridi — 4 kutu, çözüm oranı dahil ── */}
      {hasResult && <KpiStrip summary={summary} />}

      {/* ── Dataset sekmeleri ── */}
      {hasResult && (
        <DatasetTabs
          items={tabItems}
          activeKey={activeDataset}
          onChange={setActiveDataset}
        />
      )}

      {/* ── Tablo ── */}
      {hasResult && (
        <TableCard
          dataset={activeDataset}
          table={activeTable}
          columnLabel={columnLabel}
          right={<ReportsExport activeDataset={activeDataset} />}
          footer={
            showPager ? (
              <Pagination
                page={appPager.page}
                totalPages={appPager.totalPages}
                onPrev={onPrevPage}
                onNext={onNextPage}
              />
            ) : null
          }
        />
      )}

      {/* ── Eksik dataset uyarısı ── */}
      {missingRequested.length > 0 && (
        <div className="rr-state rr-state--warn">
          <ExclamationTriangleIcon width={16} height={16} />
          <div>
            <div className="rr-state__title">Bazı Tablolar Gelmedi</div>
            <div className="rr-state__body">
              İstenen ama sorgudan dönmeyen:{" "}
              <b>{missingRequested.join(", ")}</b>
              <br />
              Yetki, kontrat veya parametre yapılandırmasını kontrol edin.
            </div>
          </div>
        </div>
      )}

      {/* ── Gelişmiş hint ── */}
      {showAdvancedHint && (
        <div className="rr-state">
          <InformationCircleIcon width={16} height={16} />
          <div>
            <div className="rr-state__title">Ek Tablolar</div>
            <div className="rr-state__body">
              Gelişmiş bölümde{" "}
              <b>Durum Logları · Yönlendirme Logları · İşlem Logları · Kapatma Talep Geçmişi</b>{" "}
              dataset'lerini seçip <b>Uygula</b> ile sekmeleri açabilirsiniz.
            </div>
          </div>
        </div>
      )}

    </div>
  );
}